import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, User, Users, ScanLine, Calendar, CalendarDays, CalendarRange, ClipboardCheck, Trophy, FileText, Sprout, Medal, Settings, LogOut, QrCode, ClipboardList, Lock, UserCheck, UsersRound, MessageCircle, Shield, HelpCircle, Database, HandHeart, KeyRound, CreditCard, BarChart3, FolderOpen, Globe, Cake } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card } from '../components/ui/card';
import { LOGO_RAIZES_WHITE } from '../lib/branding';
import { canEdit, hasPermission, isFullAdmin } from '../lib/auth';
import { VerseCard } from '../components/VerseCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [topRanking, setTopRanking] = useState([]);
  const [nextMeeting, setNextMeeting] = useState(null);
  const [prayerAlerts, setPrayerAlerts] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [recoveryRequests, setRecoveryRequests] = useState([]);
  const [whatsappEquipe, setWhatsappEquipe] = useState('');
  const [birthdayCount, setBirthdayCount] = useState(0);

  useEffect(() => {
    fetchDashboardData();
    fetchPrayerAlerts();
    fetchUnreadMessages();
    if (hasPermission('administracao')) fetchRecoveryRequests();
    fetchWhatsappEquipe();
    fetchBirthdayCount();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, rankingRes, meetingRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`),
        axios.get(`${API}/ranking?period=all`),
        axios.get(`${API}/agenda/next-meeting`),
      ]);
      setStats(statsRes.data);
      setTopRanking(rankingRes.data.slice(0, 3));
      setNextMeeting(meetingRes.data.next);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const fetchPrayerAlerts = async () => {
    try {
      const res = await axios.get(`${API}/prayers/alerts`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      setPrayerAlerts(res.data.unread_count || 0);
    } catch { /* */ }
  };

  const fetchUnreadMessages = async () => {
    try {
      const res = await axios.get(`${API}/feedback/unread-count`);
      setUnreadMessages(res.data.unread || 0);
    } catch { /* */ }
  };

  const fetchRecoveryRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/recovery-requests`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      setRecoveryRequests((res.data || []).filter(r => !r.expired));
    } catch { /* */ }
  };

  const fetchWhatsappEquipe = async () => {
    try {
      const res = await axios.get(`${API}/groups/whatsapp-links`);
      setWhatsappEquipe(res.data.equipe || '');
    } catch {}
  };

  const fetchBirthdayCount = async () => {
    try {
      const res = await axios.get(`${API}/reports/birthday-messages`);
      setBirthdayCount(res.data.count || 0);
    } catch {}
  };

  const sendCodeViaWhatsApp = (request) => {
    const num = request.celular.replace(/\D/g, '');
    const fullNum = num.startsWith('55') ? num : `55${num}`;
    const msg = encodeURIComponent(`Olá ${request.nome}! Seu código de recuperação de senha do Ministério Raízes é: *${request.code}*\n\nEle expira em 15 minutos.`);
    window.open(`https://wa.me/${fullNum}?text=${msg}`, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    navigate('/login');
  };

  const generalActions = [
    { icon: ScanLine, label: 'Check-in', path: '/checkin', color: 'bg-emerald-600' },
    { icon: User, label: 'Cadastrar Familia', path: '/gestao-pais', color: 'bg-[#1A4D2E]', adminOnly: true },
    { icon: User, label: 'Participantes', path: '/participantes', color: 'bg-[#5A7A3B]' },
    { icon: QrCode, label: 'QR Code Pais', path: '/compartilhar', color: 'bg-[#1A4D2E]' },
    { icon: QrCode, label: 'QR Code Voluntários', path: '/qrcode-voluntarios', color: 'bg-[#5A7A3B]', adminOnly: true },
    { icon: UserCheck, label: 'Voluntários', path: '/voluntarios', color: 'bg-[#1A4D2E]' },
    { icon: UsersRound, label: 'Grupos Discipulado', path: '/grupos', color: 'bg-[#5A7A3B]' },
    { icon: CalendarRange, label: 'Agenda', path: '/agenda', color: 'bg-[#1A4D2E]' },
    { icon: ClipboardCheck, label: 'Escalas', path: '/escalas', color: 'bg-[#5A7A3B]' },
    { icon: Lock, label: 'Alterar Senha', path: '/alterar-senha', color: 'bg-[#1A4D2E]' },
    { icon: MessageCircle, label: 'Mensagens', path: '/feedback', color: 'bg-[#5A7A3B]' },
    { icon: HandHeart, label: 'Orações', path: '/oracoes', color: 'bg-amber-700' },
    { icon: FileText, label: 'Relatórios', path: '/relatorios?cat=ministerio', color: 'bg-[#5A7A3B]' },
    { icon: Cake, label: 'Aniversariantes', path: '/aniversariantes', color: 'bg-[#E8C74A] text-[#1A4D2E]' },
    { icon: HelpCircle, label: 'Ajuda', path: '/ajuda', color: 'bg-amber-500' },
  ];

  const sementeActions = [
    { icon: CalendarDays, label: 'Lançar Semanal', path: '/lancamento/semanal', color: 'bg-[#E8C74A] text-[#1A4D2E]', adminOnly: true },
    { icon: Sprout, label: 'Sementes Extras', path: '/sementes-extras', color: 'bg-[#E8C74A] text-[#1A4D2E]', adminOnly: true },
    { icon: Sprout, label: 'Ver Pontuação', path: '/pontuacao', color: 'bg-[#5A7A3B]' },
    { icon: Trophy, label: 'Ranking', path: '/ranking', color: 'bg-[#1A4D2E]' },
    { icon: FileText, label: 'Relatórios', path: '/relatorios?cat=sementes', color: 'bg-[#5A7A3B]' },
    { icon: Settings, label: 'Configurações', path: '/configuracoes', color: 'bg-[#1A4D2E]' },
  ];

  const adminActions = [
    { icon: Users, label: 'Usuários', path: '/usuarios', color: 'bg-[#1A4D2E]', adminOnly: true },
    { icon: UsersRound, label: 'Gestão de Pais', path: '/gestao-pais', color: 'bg-[#5A7A3B]', adminOnly: true },
    { icon: FolderOpen, label: 'Gestão de Áreas', path: '/gestao-areas', color: 'bg-[#1A4D2E]', adminOnly: true },
    { icon: MessageCircle, label: 'Links WhatsApp', path: '/admin/whatsapp', color: 'bg-[#25D366]', adminOnly: true },
    { icon: CreditCard, label: 'Mensalidades', path: '/mensalidades', color: 'bg-emerald-700', adminOnly: false },
    { icon: Shield, label: 'Backups', path: '/backups', color: 'bg-gray-700', adminOnly: true },
    { icon: Database, label: 'Transferir Dados', path: '/dados', color: 'bg-gray-600', adminOnly: true },
    { icon: ClipboardList, label: 'Logs', path: '/logs', color: 'bg-gray-500', adminOnly: true },
    { icon: Globe, label: 'Editar Landing Page', path: '/editar-landing', color: 'bg-teal-700', adminOnly: true },
  ];

  const filterByRole = (actions) => {
    const editable = hasPermission('edicao');
    return editable ? actions : actions.filter(a => !a.adminOnly);
  };

  const showSementes = hasPermission('projeto_sementes');
  const showAdmin = hasPermission('administracao');

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-6 pt-8 pb-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_RAIZES_WHITE} alt="Ministério Raízes" className="h-10" />
              <div>
                <h1 className="font-nunito font-bold text-3xl md:text-4xl">
                  Ministerio Raizes
                </h1>
                <p className="font-inter text-white/70 text-sm">Painel administrativo</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/perfil-admin')}
                className="hover:bg-white/10 rounded-full p-2 transition-all"
                title="Meu Perfil"
              >
                <User className="w-6 h-6" />
              </button>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="hover:bg-white/10 rounded-full p-2 transition-all"
                title="Sair"
              >
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card data-testid="stat-criancas" className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-inter text-sm text-gray-500 mb-1">Total de Crianças</p>
                <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">
                  {stats?.total_criancas || 0}
                </p>
              </div>
              <div className="bg-[#F2F7F4] rounded-full p-3">
                <User className="w-6 h-6 text-[#1A4D2E]" />
              </div>
            </div>
          </Card>

          <Card data-testid="stat-sementes" className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-inter text-sm text-gray-500 mb-1">Total de Sementes</p>
                <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">
                  {stats?.total_sementes || 0}
                </p>
              </div>
              <div className="bg-[#FEFAE6] rounded-full p-3">
                <Sprout className="w-6 h-6 text-[#F4D35E]" />
              </div>
            </div>
          </Card>

          <Card data-testid="stat-top-crianca" className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-inter text-sm text-gray-500 mb-1">Maior Pontuação</p>
                <p className="font-nunito font-bold text-lg text-[#1A4D2E] truncate">
                  {stats?.top_crianca?.nome || 'N/A'}
                </p>
                <p className="font-inter text-sm text-[#1A4D2E] font-semibold">
                  {stats?.top_crianca?.pontos || 0} sementes
                </p>
              </div>
              <div className="bg-[#FEFAE6] rounded-full p-3">
                <Medal className="w-6 h-6 text-[#F4D35E]" />
              </div>
            </div>
          </Card>
        </div>

        {/* Next Meeting Card */}
        {nextMeeting && (
          <Card data-testid="next-meeting-card" className="bg-gradient-to-r from-[#F4D35E] to-[#E8C74A] rounded-2xl p-5 shadow-lg mb-8 text-[#1A4D2E] cursor-pointer hover:shadow-xl transition-all" onClick={() => navigate('/agenda')}>
            <div className="flex items-start gap-4">
              <div className="bg-[#1A4D2E]/15 rounded-xl p-3 shrink-0">
                <CalendarRange className="w-7 h-7 text-[#1A4D2E]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-inter text-[#1A4D2E]/60 text-xs uppercase tracking-wider mb-1">Proximo Encontro</p>
                <p className="font-nunito font-bold text-xl text-[#1A4D2E]">
                  {nextMeeting.data ? new Date(nextMeeting.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' }) : `Semana ${nextMeeting.semana}`}
                </p>
                {(nextMeeting.horario_inicio || nextMeeting.horario) && (
                  <p className="font-inter text-[#1A4D2E]/70 text-sm mt-0.5">
                    {nextMeeting.horario_inicio || nextMeeting.horario}{nextMeeting.horario_fim ? ` - ${nextMeeting.horario_fim}` : ''}
                  </p>
                )}
                <p className="font-inter text-[#1A4D2E] font-semibold text-sm mt-2">{nextMeeting.programacao || 'A definir'}</p>
                <p className="font-inter text-[#1A4D2E]/70 text-sm">Tema: {nextMeeting.tema || 'A definir'}</p>
                {nextMeeting.pregador && (
                  <p className="font-inter text-[#1A4D2E]/60 text-sm">Pregador: {nextMeeting.pregador}</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#1A4D2E]/50">
                  {nextMeeting.louvor_nomes?.length > 0 && <span>Louvor: {nextMeeting.louvor_nomes.join(', ')}</span>}
                  {nextMeeting.checkin_nomes?.length > 0 && <span>Checkin: {nextMeeting.checkin_nomes.join(', ')}</span>}
                  {nextMeeting.sementes_nomes?.length > 0 && <span>Sementes: {nextMeeting.sementes_nomes.join(', ')}</span>}
                  {nextMeeting.midia_nomes?.length > 0 && <span>Mídia: {nextMeeting.midia_nomes.join(', ')}</span>}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Verse of the Week */}
        <div className="mb-8">
          <VerseCard isAdmin={true} />
        </div>

        {/* Recovery Requests Alert Card */}
        {recoveryRequests.length > 0 && (
          <Card data-testid="recovery-alert-card" className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl p-5 shadow-lg mb-8 text-white">
            <div className="flex items-center gap-4 mb-3">
              <div className="bg-white/15 rounded-xl p-3 shrink-0">
                <KeyRound className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-nunito font-bold text-lg">Recuperacao de Senha</p>
                <p className="text-white/70 text-sm">{recoveryRequests.length} solicitacao(oes) pendente(s)</p>
              </div>
            </div>
            <div className="space-y-2">
              {recoveryRequests.map((req, idx) => (
                <div key={idx} className="bg-white/10 rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">{req.nome || req.celular}</p>
                    <p className="text-white/60 text-xs">{req.celular} - Codigo: <span className="font-mono font-bold text-white">{req.code}</span></p>
                  </div>
                  <button onClick={() => sendCodeViaWhatsApp(req)} data-testid={`send-code-whatsapp-${idx}`}
                    className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all">
                    <MessageCircle className="w-3.5 h-3.5" /> Enviar via WhatsApp
                  </button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Prayer Requests Alert Card */}
        <Card
          data-testid="prayer-alert-card"
          className="bg-gradient-to-r from-amber-700 to-amber-600 rounded-2xl p-5 shadow-lg mb-8 text-white cursor-pointer hover:shadow-xl transition-all"
          onClick={() => navigate('/oracoes')}
        >
          <div className="flex items-center gap-4">
            <div className="bg-white/15 rounded-xl p-3 shrink-0 relative">
              <HandHeart className="w-6 h-6 text-white" />
              {prayerAlerts > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="prayer-alert-badge">
                  {prayerAlerts}
                </span>
              )}
            </div>
            <div className="flex-1">
              <p className="font-nunito font-bold text-lg">Pedidos de Oração</p>
              <p className="text-white/70 text-sm">
                {prayerAlerts > 0 ? `${prayerAlerts} novo(s) pedido(s) aguardando` : 'Acompanhe e interceda pelos pedidos'}
              </p>
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-4">Ministério</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {filterByRole(generalActions).map((action, idx) => (
              <button
                key={idx}
                data-testid={`quick-action-general-${idx}`}
                onClick={() => navigate(action.path)}
                className={`${action.color} text-white rounded-2xl p-6 shadow-md hover:shadow-xl active:scale-95 transition-all duration-300 relative`}
              >
                {action.label === 'Mensagens' && unreadMessages > 0 && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center animate-pulse" data-testid="unread-messages-badge">
                    {unreadMessages}
                  </span>
                )}
                {action.label === 'Aniversariantes' && birthdayCount > 0 && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center animate-pulse" data-testid="birthday-count-badge">
                    {birthdayCount}
                  </span>
                )}
                <action.icon className="w-8 h-8 mx-auto mb-3" strokeWidth={2} />
                <p className="font-inter font-semibold text-sm">{action.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* WhatsApp Equipe */}
        <div className="mb-6">
          <a
            href={whatsappEquipe || "https://chat.whatsapp.com/BaePQNHMzG01wTO0czeJok?mode=gi_t"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-equipe-btn"
            className="w-full flex items-center gap-4 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-2xl p-5 shadow-md hover:shadow-xl active:scale-[0.98] transition-all"
          >
            <MessageCircle className="w-8 h-8" strokeWidth={2} />
            <div>
              <p className="font-nunito font-bold text-base">Grupo da Equipe</p>
              <p className="font-inter text-white/80 text-xs">Entrar no grupo WhatsApp dos voluntarios</p>
            </div>
          </a>
        </div>

        {showSementes && (
        <div className="mb-8">
          <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-4">Projeto Sementes</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {filterByRole(sementeActions).map((action, idx) => (
              <button
                key={idx}
                data-testid={`quick-action-sementes-${idx}`}
                onClick={() => navigate(action.path)}
                className={`${action.color} text-white rounded-2xl p-6 shadow-md hover:shadow-xl active:scale-95 transition-all duration-300`}
              >
                <action.icon className="w-8 h-8 mx-auto mb-3" strokeWidth={2} />
                <p className="font-inter font-semibold text-sm">{action.label}</p>
              </button>
            ))}
          </div>
        </div>
        )}

        {showAdmin && (
          <div className="mb-8">
            <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-4">Administração</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {adminActions.map((action, idx) => (
                <button
                  key={idx}
                  data-testid={`quick-action-admin-${idx}`}
                  onClick={() => navigate(action.path)}
                  className={`${action.color} text-white rounded-2xl p-6 shadow-md hover:shadow-xl active:scale-95 transition-all duration-300`}
                >
                  <action.icon className="w-8 h-8 mx-auto mb-3" strokeWidth={2} />
                  <p className="font-inter font-semibold text-sm">{action.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Top 3 Ranking */}
        <div className="mb-8">
          <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-4">Top 3 do Ranking</h2>
          {topRanking.length > 0 ? (
            <div className="space-y-3">
              {topRanking.map((item, idx) => {
                const medals = ['🥇', '🥈', '🥉'];
                return (
                  <Card
                    key={item.child_id}
                    data-testid={`ranking-item-${idx}`}
                    className="bg-white rounded-2xl p-5 shadow-md border border-[#E1ECE5] hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => navigate(`/participante/${item.child_id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-3xl">{medals[idx]}</span>
                        <div>
                          <p className="font-nunito font-bold text-lg text-[#1A4D2E]">
                            {item.child_name}
                          </p>
                          <p className="font-inter text-sm text-gray-500">
                            {item.posicao}º lugar
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-nunito font-bold text-2xl text-[#1A4D2E]">
                          {item.total_pontos}
                        </p>
                        <p className="font-inter text-xs text-gray-500">sementes</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="bg-white rounded-2xl p-8 text-center shadow-md border border-[#E1ECE5]">
              <p className="font-inter text-gray-500">Nenhuma criança cadastrada ainda</p>
            </Card>
          )}
        </div>

        {/* Top 3 Ranking */}
      </div>

      <Navigation active="dashboard" />
    </div>
  );
};

export default Dashboard;
